import {
    Container,
    Rectangle,
    Sprite,
    Texture,
    InteractionEvent
} from 'pixi.js';
import { Game } from '../../game';
import { Scene } from '../Scene';
import { Button, ActiveButton, FocusManager, InteractiveWrapper } from '../../ui';
import { Disposable, DisposableGroup, Music } from '../../utils';
import KeyboardJS from 'keyboardjs';
import { PingAnimation } from '../04-game';

interface ImagePageData
{
    image:string;
    vo:string;
    ad:string|string[];
    /**
     * Some of the AD ends with a line about the next button pulsing, so we want to play that
     * after the VO, instead of before
     */
    adAfter?: string|string[];
    ping?: boolean;
}
interface VideoPageData
{
    video:string;
    poster: string;
    vo?: string;
    ad:string|string[];
    /**
     * Some of the AD ends with a line about the next button pulsing, so we want to play that
     * after the VO, instead of before
     */
    adAfter?: string|string[];
    muteMusic?: boolean;
}
type PageData = ImagePageData | VideoPageData;

export class StoryScene extends Scene
{
    private background: Sprite;

    private pageData: PageData[];
    private pageIndex: number;

    private nextButton: Button;
    private backButton: Button;
    private playButton: ActiveButton;
    private pingAnim: PingAnimation;
    private music: Music;

    private settingsButton: Button;
    private restArea: Button;

    private gestureCatcher: Container;
    private disposables: DisposableGroup;
    /**
     * Only attempt to unpause once, because if it happens repeatedly is probably a network issue
     * we can't do anything abouy
     */
    private attemptedUnpause: boolean;

    private isIntro: boolean;

    constructor(game:Game, isIntro = true)
    {
        super(game);

        this.isIntro = isIntro;
        this.attemptedUnpause = false;
    }

    async preload()
    {
        await super.preload();
        const {loader} = this;
        loader.add('StoryAtlas', './assets/story/StoryAtlas{size}.json');
        loader.add('SharedUI', './assets/shared/SharedUI{size}.json');
        Button.preload();

        if (this.isIntro)
        {
            this.pageData = [
                {
                    video: "CYB_IntroVideo",
                    poster: "CYB120_02-05_v3-Fix.jpg",
                    ad: ["CYB_Narrativeintro_Screen1_AD", "CYB_GameIntroOutro_Settings"]
                },
                {
                    image: "narrative_01",
                    vo: "CYB_Narrativeintro_Screen2",
                    ad: ""
                },
                {
                    image: "narrative_02",
                    vo: "CYB_Narrativeintro_Screen3",
                    ad: "CYB_Narrativeintro_Screen2BAD"
                },
                {
                    image: "narrative_03",
                    vo: "CYB_Narrativeintro_Screen4",
                    ad: "CYB_Narrativeintro_Screen4AD"
                },
                {
                    image: "narrative_04",
                    vo: "CYB_Narrativeintro_Screen5",
                    adAfter: ['CYB_Narrativeintro_Screen5AD','CYB_GameIntroOutro_Settings'],
                    ad: '',
                },
                {
                    image: "narrative_05",
                    vo: "CYB_Narrativeintro_Screen6",
                    ping: true,
                    adAfter: ['CYB_Narrativeintro_Screen6AD','CYB_GameIntroOutro_Settings'],
                    ad: '',
                }
            ];
            loader.add('narrative_01', './assets/story/narrative_01.jpg');
            loader.add('narrative_02', './assets/story/narrative_02.jpg');
            loader.add('narrative_03', './assets/story/narrative_03.jpg');
            loader.add('narrative_04', './assets/story/narrative_04.jpg');
            loader.add('narrative_05', './assets/story/narrative_05.jpg');
            loader.add('music', './assets/music/Cyb120_Final_Stereo_Music_20191206_Story.{opus,caf,mp3}');
            PingAnimation.preload();
        }
        else
        {
            this.pageData = [
                {
                    video: "CYB_EndScreen_4",
                    poster: "CYB120_BatsOut_v3-Fix-Short.jpg",
                    ad: "CYB_EndScreen_1_AD",
                    muteMusic: true
                },
                // Inserted image because the video makes this weird
                {
                    image: "narrative_06",
                    vo: "CYB_EndScreen_1",
                    ad: ""
                },
                // normal story continues
                {
                    image: "narrative_06",
                    vo: "CYB_EndScreen_2",
                    ad: "CYB_EndScreen_2_AD"
                },
                {
                    image: "narrative_07",
                    vo: "CYB_EndScreen_3",
                    ad: "CYB_EndScreen_3_AD"
                },
                {
                    video: "CYB120_259-260_Fix.mp4",
                    poster: "CYB120_259-260_Fix.jpg",
                    ad: "CYB_EndScreen_4_AD1",
                    adAfter: 'CYB_EndScreen_4_AD2',
                    muteMusic: true
                }
            ];
            loader.add('narrative_06', './assets/story/narrative_06.jpg');
            loader.add('narrative_07', './assets/story/narrative_07.jpg');
            loader.add('music', './assets/music/Cyb120_Final_Stereo_Music_20191206_End.{opus,caf,mp3}');
        }

        const {videoElement} = this.game;
        videoElement.autoplay = false;
        videoElement.controls = false;
        let loadVideo: Promise<any>;
        const firstPage = this.pageData[0];
        if ('video' in firstPage)
        {
            videoElement.poster = `./assets/story/${firstPage.poster}`;
            loadVideo = this.game.vo.preloadVideo(videoElement, firstPage.video);
        }

        if (loadVideo)
        {
            await Promise.all([loader.load(), loadVideo]);
        }
        else
        {
            await loader.load();
        }

        this.setup();
    }

    private setup()
    {
        this.pageIndex = 0;

        this.music = new Music(this.loader.getSound('music'), this.game);

        // Keyboard input
        KeyboardJS.setContext('title');
        const keyListeners = {
            space: (e:KeyboardJS.KeyEvent) => {
                e.preventRepeat();

                this.requestAudioDescription();
            }
        }

        this.disposables = new DisposableGroup();
        // keys bound in a context are attached to just that context
        for (const key in keyListeners)
        {
            this.disposables.add(Disposable.fromKey(key, keyListeners[key]));
        }

        // Tap Input
        this.gestureCatcher = new Container();
        this.addChild(this.gestureCatcher);
        this.gestureCatcher.interactive = true;
        this.gestureCatcher.hitArea = new Rectangle(0, 0, 2160, 1080);
        this.gestureCatcher.on( 'pointertap', (evt: InteractionEvent) => {
            if( evt.data.global.y > 540) {
                // Bottom half of screen.
                this.requestAudioDescription();
            }
        });

        this.playButton = new ActiveButton(
            this.loader.getTexture('btn_video_pause', 'StoryAtlas'),
            {
                content: 'PAUSE',
                style: {
                    fontName: 'Asap',
                    fontSize: 38,
                    tint: 0x000000,
                },
                offset: {x: 20, y: 0}
            },
            this.loader.getTexture('btn_video_play', 'StoryAtlas'),
            {
                content: 'PLAY',
                style: {
                    fontName: 'Asap',
                    fontSize: 38,
                    tint: 0x000000,
                },
                offset: {x: 20, y: 0}
            }
        );
        this.playButton.position.set(1085, 996);
        this.addChild(this.playButton);
        this.disposables.add(new InteractiveWrapper(this.playButton, {
            onHover: () => {
                this.game.vo.playHighlight(this.playButton.active ? 'CYB_Narrative_Play' : 'CYB_Narrative_Pause');
            },
            onTap: async () => {
                const paused = !this.playButton.active;
                this.playButton.active = paused;
                if (paused)
                {
                    this.game.vo.pause(true);
                }
                else
                {
                    this.game.vo.unpause(true);
                }
            }
        }));
        this.playButton.visible = false;
        const onPause = () => {
            const thisPage = this.pageData[ this.pageIndex ];
            if (!('video' in thisPage) || !this.game.vo.isPlaying(thisPage.video)) return;
            // if we haven't auto-unpaused yet this story scene and the button isn't in pause mode
            // (the user didn't pause via the pause button)
            if (!this.attemptedUnpause && !this.playButton.active)
            {
                this.attemptedUnpause = true;
                this.game.videoElement.play();
            }
            else
            {
                this.playButton.active = true;
                this.game.vo.pause(true);
            }
        };
        this.game.videoElement.addEventListener('pause', onPause);
        this.disposables.add(new Disposable(() => this.game.videoElement.removeEventListener('pause', onPause)));

        this.backButton = new Button(
            this.loader.getTexture('btn_back', 'StoryAtlas'),
            {
                content: 'Back',
                style: {
                    fontName: 'Asap',
                    fontSize: 38,
                    tint: 0x000000,
                },
                offset: {x: 30, y: -2}
            }
        );
        this.disposables.add(new InteractiveWrapper(this.backButton, {
            onHover: () => {
                this.game.vo.playHighlight( "CYB_Instructions_Back" );
            },
            onTap: () => {
                if (this.pageIndex === 0)
                {
                    this.game.returnToTitle();
                }
                else
                {
                    --this.pageIndex;
                    this.displayCurrentPage();
                }
            }
        }));
        this.backButton.position.set( 789, 996 );
        this.addChild( this.backButton );

        this.nextButton = new Button(
            this.loader.getTexture('btn_next', 'SharedUI'),
            {
                content: 'Next',
                style: {
                    fontName: 'Asap',
                    fontSize: 38,
                    tint: 0x000000,
                },
                offset: {x: -30, y: -2}
            }
        );
        this.disposables.add(new InteractiveWrapper(this.nextButton, {
            onHover: () => {
                this.game.vo.playHighlight( "CYB_Instructions_Next" );
            },
            onTap: () => {
                this.game.vo.stop();
                ++this.pageIndex;
                this.displayCurrentPage();
            }
        }));
        this.nextButton.position.set( 1381, 996 );
        this.addChild( this.nextButton );

        this.settingsButton = new Button(
            this.loader.getTexture('btn_settings', 'SharedUI')
        );
        this.disposables.add(new InteractiveWrapper(this.settingsButton, {
            onHover: () => {
                this.game.vo.playHighlight("CYB_Game_Settings");
            },
            onTap: () => {
                this.game.vo.stop();

                // Disable buttons.
                this.settingsButton.enabled = false;
                this.playButton.enabled = false;
                this.nextButton.enabled = false;
                this.backButton.enabled = false;

                // Open panel.
                this.game.showAccessibilityPopover( ()=>{
                    // Enable buttons.
                    this.settingsButton.enabled = true;
                    this.playButton.enabled = true;
                    this.nextButton.enabled = true;
                    this.backButton.enabled = true;
                    // Replay current page.
                    this.displayCurrentPage();
                });
            }
        }));
        this.settingsButton.position.set(1700, 996);
        this.settingsButton.createHitCircle(30);
        this.addChild(this.settingsButton);

        if (this.isIntro)
        {
            this.pingAnim = new PingAnimation(this.game);
            this.pingAnim.position.set(1238, 720);
            this.pingAnim.visible = false;
            this.addChild(this.pingAnim);
        }

        FocusManager.instance.activateContext([this.playButton, this.backButton, this.nextButton, this.settingsButton]);
    }

    private async displayCurrentPage()
    {
        this.game.vo.overrideInterrupt = true;
        this.nextButton.highlight = false;
        this.playButton.visible = false;

        if (this.pageIndex >= this.pageData.length)
        {
            // Begin game.
            if (this.isIntro)
            {
                this.game.nextLevel(0);
            }
            else
            {
                this.game.returnToTitle();
            }
        }
        else
        {
            const thisPage = this.pageData[ this.pageIndex ];

            if ('muteMusic' in thisPage && thisPage.muteMusic)
            {
                this.music.stop();
            }
            else if (!this.music.playing)
            {
                this.music.play();
            }

            let videoLoad: Promise<any>;
            if ('image' in thisPage)
            {
                // Update background image.
                const texture = this.loader.getAsset<Texture>(thisPage.image);
                if (this.background)
                {
                    this.background.texture = texture;
                    this.background.visible = true;
                }
                else
                {
                    this.background = new Sprite( texture );
                    this.addChildAt( this.background, 0 );
                }

                // show ping anim if we need to
                if (thisPage.ping)
                {
                    this.pingAnim.play(480, 120);
                    this.pingAnim.visible = true;
                }
                else if (this.pingAnim?.visible)
                {
                    this.pingAnim.stop();
                    this.pingAnim.visible = false;
                }
            }
            else
            {
                if (this.background)
                {
                    this.background.visible = false;
                }
                const src = `./assets/story/${thisPage.video}`;
                const {videoElement} = this.game;
                if (videoElement.src !== src)
                {
                    videoLoad = this.game.vo.preloadVideo(videoElement, thisPage.video);
                }
            }

            // Play audio
            if (this.shouldPlayAD && thisPage.ad)
            {
                await this.game.vo.playVO( thisPage.ad, true );
            }
            if ('video' in thisPage)
            {
                await videoLoad;
                this.playButton.visible = true;
                this.playButton.active = false;
                if(this.game.app.state.voVolume.value > 0)
                {
                    await this.game.vo.playVideo(this.game.videoElement, thisPage.video);
                }
                else 
                {
                    await this.game.vo.playVideo(this.game.videoElement, 'Silence');
                }
                this.playButton.visible = false;
                if (thisPage.vo)
                {
                    await this.game.vo.playVO(thisPage.vo, true);
                }
            }
            else
            {
                await this.game.vo.playVO( thisPage.vo, true );
            }
            // if they waited for all the audio, highlight the next button to make sure they
            // don't get lost
            this.nextButton.highlight = true;
            if (this.shouldPlayAD)
            {
                if (thisPage.adAfter)
                {
                    await this.game.vo.playVO( thisPage.adAfter, true );
                }
                else if (this.pageIndex === 0)
                {
                    this.game.vo.playVO('CYB_Narrativeintro_Screen2AD', true);
                }
                else
                {
                    this.game.vo.playVO(['Cyberchase_Game_NextBack_AD', 'CYB_GameIntroOutro_Settings'], true);
                }
            }
        }
    }

    private requestAudioDescription()
    {
        if (!this.game.vo.playing && this.shouldPlayAD)
        {
            const thisPage = this.pageData[ this.pageIndex ];
            this.game.vo.playVO( thisPage.ad, true );
        }
    }

    start()
    {
        // Loader anim is complete, scene can start
        this.displayCurrentPage();
    }

    update(_deltaTime:number)
    {
        // nothing to do
    }

    cleanup()
    {
        this.music.destroy();

        this.game.vo.stop();
        this.game.videoElement.src = '';

        super.cleanup();
        FocusManager.instance.popContext();

        KeyboardJS.setContext('global');
        this.disposables.destroy();
        super.destroy({children: true});
    }
}
